import {DataTableColumn} from 'mantine-datatable';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Group,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';

import {CertificateStatusBadge} from '../components/CertificateStatusBadge';
import {
    Certificate,
    isWildcard,
} from '../types';

// FNP-1193 — THE COLUMNS. The ticket names exactly what a user needs to see:
// which names a certificate covers, whether it is issued or pending or failed,
// and when it expires.

/**
 * The expiry cell's text.
 *
 * ⚠️ NULL IS NORMAL AND MUST NOT RENDER AS AN ERROR OR A DATE.
 *
 * `expirationDate` is read through to the provider and a certificate that has
 * not issued yet HAS no expiry — there is nothing to expire. An em dash says
 * "not applicable"; a fabricated date or the word "invalid" would both be
 * claims the provider never made.
 *
 * Exported for its own test: the null case is the one that regresses.
 */
export const expiryText = (certificate: Certificate): string => {
    if (!certificate.expirationDate) {
        return '—';
    }
    // The raw AWSDateTime is an ISO-8601 string. Rendering the date part alone
    // is what an operator reads an expiry as; the time of day is noise at a
    // 13-month horizon.
    return certificate.expirationDate.slice(0, 10);
};

export const useCertificatesTableColumns = () => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<Certificate>[] = [
        {
            accessor: 'domainName',
            sortable: true,
            title: I18n.get('Certificate'),
            // ⚠️ THE PRIMARY NAME AND WHAT ELSE IS COVERED, IN ONE CELL.
            //
            // "Which names does this certificate cover" is the first question
            // anyone asks of a certificate list, and answering it only on a
            // detail page means opening every row to find the one that covers
            // the hostname you care about.
            render: (item: Certificate) => {

                const covered = item.coveredNames ?? [];
                // The primary is already the heading; the rest are the extra
                // information.
                const additional = covered.filter(name => name !== item.domainName);

                return (
                    <Stack gap={2}>
                        <Group gap={'xs'} wrap={'nowrap'}>
                            <Text size={'sm'} fw={500}>{item.domainName}</Text>
                            {/* ⚠️ A WILDCARD IS MARKED, and the tooltip carries
                                the one-label rule. This is one of the two
                                places the ticket asks for that constraint to be
                                explained "at the point in the interface where
                                it applies" — the other is the request form.
                                Here it answers "why does this certificate not
                                cover my deeper hostname". */}
                            {isWildcard(item.domainName) && (
                                <Tooltip
                                    multiline={true}
                                    w={300}
                                    label={I18n.get('A wildcard covers exactly one label. This certificate covers names directly beneath its parent, but not a name two levels down — that needs its own name on a certificate.')}
                                >
                                    <Badge variant={'outline'} color={'grape'} size={'sm'}>
                                        {I18n.get('Wildcard')}
                                    </Badge>
                                </Tooltip>
                            )}
                        </Group>
                        {additional.length > 0 && (
                            <Text size={'xs'} c={'dimmed'}>
                                {I18n.get('also covers')}: {additional.join(', ')}
                            </Text>
                        )}
                    </Stack>
                );
            },
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            // ⚠️ THE CELL THE TICKET IS REALLY ABOUT. Four states that look
            // alike and are not — see CertificateStatusBadge for why only one
            // of them gets a spinner.
            render: (item: Certificate) => (
                <CertificateStatusBadge certificate={item}/>
            ),
        },
        {
            accessor: 'targetType',
            sortable: true,
            title: I18n.get('Target'),
            // ⚠️ THE TARGET AND THE REGION TOGETHER, because one explains the
            // other. A CloudFront certificate in us-east-1 looks arbitrary
            // until you can see that CloudFront is what put it there.
            render: (item: Certificate) => (
                <Stack gap={2}>
                    <Text size={'sm'}>{targetLabel(item.targetType)}</Text>
                    <Text size={'xs'} c={'dimmed'}>{item.region}</Text>
                </Stack>
            ),
        },
        {
            accessor: 'expirationDate',
            sortable: true,
            title: I18n.get('Expires'),
            // ⚠️ AN EM DASH FOR A CERTIFICATE THAT HAS NOT ISSUED, never a
            // fabricated date. It is read through to the provider and a pending
            // certificate genuinely has no expiry.
            render: (item: Certificate) => (
                <Text size={'sm'} c={item.expirationDate ? undefined : 'dimmed'}>
                    {expiryText(item)}
                </Text>
            ),
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Requested'),
            render: (item: Certificate) => (
                <Text size={'sm'}>{item.created ? item.created.slice(0, 10) : '—'}</Text>
            ),
        },
    ];

    return {
        columns,
    };
};

// What each target means in words. `UNSPECIFIED` is a real and supported
// choice — a tenant managing a certificate without telling Flexi what consumes
// it — so it gets an honest label rather than reading as missing data.
const targetLabel = (targetType: Certificate['targetType']): string => {
    switch (targetType) {
        case 'CLOUDFRONT':
            return I18n.get('CloudFront');
        case 'LOAD_BALANCER':
            return I18n.get('Load balancer');
        case 'API_GATEWAY':
            return I18n.get('API Gateway');
        default:
            return I18n.get('Not specified');
    }
};
